import { Link } from "react-router-dom";
import { ArrowRight, Info, ArrowUpRight, Download } from "lucide-react";
import Seo from "@/components/Seo";

const GOTEAMUP_URL = "https://goteamup.com/p/13190668-kore-wellbeing/";

// Kinds ---------------------------------------------------------
const KINDS = {
  class:   { label: "Kore Class",         dot: "bg-kore-forest",     text: "text-kore-forest",   border: "border-kore-forest/25" },
  pt:      { label: "Small-Group Coaching", dot: "bg-kore-light",    text: "text-kore-forest",   border: "border-kore-light" },
  gym:     { label: "Open Gym",           dot: "bg-kore-gold",       text: "text-kore-gold",     border: "border-kore-gold/30" },
  closed:  { label: "Closed",             dot: "bg-kore-ink/25",     text: "text-kore-ink/50",   border: "border-kore-ink/15" },
};

// Real weekly schedule from the master PDF -------------------------
const WEEK = [
  { day: "Monday", items: [
    ["06:00 – 09:00", "pt",    "Group Coaching",       "6am · 7am · 8am groups"],
    ["09:00 – 18:00", "gym",   "Open Gym",             "Staffed access"],
    ["18:00 – 19:00", "class", "Kore Kombat",          "Pad work · pairs"],
  ]},
  { day: "Tuesday", items: [
    ["06:00 – 09:00", "pt",    "Group Coaching",       "6am · 7am · 8am groups"],
    ["09:00 – 14:00", "gym",   "Open Gym",             "Staffed access"],
    ["19:00 – 20:00", "pt",    "Women's Strength Foundations", "Coached by Amy"],
  ]},
  { day: "Wednesday", items: [
    ["06:00 – 09:00", "pt",    "Group Coaching",       "6am · 7am · 8am groups"],
    ["09:00 – 18:00", "gym",   "Open Gym",             "Staffed access"],
    ["18:00 – 18:45", "class", "Kore Blast",           "45 min · core"],
  ]},
  { day: "Thursday", items: [
    ["06:00 – 11:00", "pt",    "Group Coaching",       "Morning groups"],
    ["11:00 – 18:00", "gym",   "Open Gym",             "Staffed access"],
    ["18:00 – 19:00", "class", "Kore Ascent",          "Hill-inspired conditioning"],
  ]},
  { day: "Friday", items: [
    ["06:00 – 09:00", "pt",    "Group Coaching",       "6am · 7am · 8am groups"],
    ["09:00 – 14:00", "gym",   "Open Gym",             "Staffed access"],
  ]},
  { day: "Saturday", items: [
    ["08:30 – 14:00", "gym",   "Open Gym",             "Weekend hours"],
  ]},
  { day: "Sunday", items: [
    ["08:30 – 12:00", "gym",   "Open Gym",             "Weekend hours"],
  ]},
];

export const OPEN_GYM_HOURS = [
  ["Monday", "09:00 – 18:00"],
  ["Tuesday", "09:00 – 14:00"],
  ["Wednesday", "09:00 – 18:00"],
  ["Thursday", "11:00 – 18:00"],
  ["Friday", "09:00 – 14:00"],
  ["Saturday", "08:30 – 14:00"],
  ["Sunday", "08:30 – 12:00"],
];

export const COACHING_HOURS = [
  ["Monday", "06:00 – 09:00"],
  ["Tuesday", "06:00 – 09:00 · 19:00 – 20:00"],
  ["Wednesday", "06:00 – 09:00"],
  ["Thursday", "06:00 – 11:00"],
  ["Friday", "06:00 – 09:00"],
];

const Legend = () => (
  <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
    {Object.entries(KINDS).map(([k, v]) => (
      <span key={k} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-kore-ink/70">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${v.dot}`} />
        {v.label}
      </span>
    ))}
  </div>
);

export default function Timetable() {
  return (
    <div>
      <Seo
        title="Weekly Timetable — coached PT, classes & open gym"
        description="See the full Kore Wellbeing weekly timetable — small-group PT, Kombat, Blast, Zones, Ascent and open-gym hours in Bowness-on-Windermere."
        path="/timetable"
      />
      <section className="container-kore pt-16 pb-8">
        <span className="eyebrow">The timetable</span>
        <h1 className="display-1 mt-4 text-kore-forest max-w-4xl">
          One quiet week. <em className="italic text-kore-gold not-italic">Everything you need.</em>
        </h1>
        <p className="mt-6 text-kore-ink/70 max-w-2xl font-light leading-relaxed">
          Coached small-group sessions, Kore classes and open gym hours in one place. Bookings are handled by GoTeamUp — hit any slot on the live calendar or head straight to the booking page.
        </p>
        <p className="mt-3 text-xs text-kore-ink/55 max-w-2xl">
          Coaching runs in fixed weekly slots — max ten members per coach — with your specific slot agreed at your consultation. Classes are included for Kore Coaching members and bookable through TeamUp. During class times the main gym floor is dedicated to the class in session.
        </p>
      </section>

      <section data-testid="timetable-legend" className="container-kore pb-10">
        <Legend />
      </section>

      <section data-testid="timetable-grid" className="container-kore pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
          {WEEK.map((d) => (
            <div key={d.day} data-testid={`day-${d.day.toLowerCase()}`} className="bg-white rounded-3xl border border-kore-gold/15 p-5 md:p-6 flex flex-col">
              <div className="pb-3 mb-3 border-b border-kore-gold/25">
                <div className="font-display text-xl text-kore-forest">{d.day}</div>
              </div>
              <ul className="space-y-3 flex-1">
                {d.items.map(([time, kind, title, sub], i) => {
                  const k = KINDS[kind];
                  const bookable = kind === "class" || kind === "pt";
                  const hoverCls = bookable ? "transition hover:shadow-soft hover:-translate-y-0.5 cursor-pointer" : "";
                  const inner = (
                    <>
                      <div className="flex items-center justify-between gap-2 text-[10px] uppercase tracking-[0.18em] text-kore-ink/50">
                        <span className="flex items-center gap-2">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${k.dot}`} />
                          {time}
                        </span>
                        {bookable && <ArrowUpRight size={11} className={k.text} />}
                      </div>
                      <div className={`mt-1.5 font-display text-lg leading-tight ${k.text}`}>{title}</div>
                      <div className="text-[11px] text-kore-ink/60 mt-0.5">{sub}</div>
                    </>
                  );
                  if (kind === "class") {
                    return (
                      <li key={i}>
                        <a
                          href={GOTEAMUP_URL}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`slot-book-${kind}`}
                          className={`block rounded-2xl border ${k.border} p-3 ${hoverCls}`}
                        >
                          {inner}
                        </a>
                      </li>
                    );
                  }
                  if (kind === "pt") {
                    return (
                      <li key={i}>
                        <Link
                          to="/coaching-enquiry"
                          data-testid={`slot-enquire-${kind}`}
                          className={`block rounded-2xl border ${k.border} p-3 ${hoverCls}`}
                        >
                          {inner}
                        </Link>
                      </li>
                    );
                  }
                  return (
                    <li key={i} className={`rounded-2xl border ${k.border} p-3`}>
                      {inner}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="container-kore py-16 grid md:grid-cols-2 gap-10">
        <div className="bg-kore-forest text-kore-warm rounded-3xl p-8 md:p-10" data-testid="timetable-hours">
          <div className="eyebrow text-kore-light">Open gym hours</div>
          <div className="mt-4 text-kore-warm/85 leading-relaxed">
            {OPEN_GYM_HOURS.map(([d, h]) => (
              <div key={d} className="flex justify-between border-b border-kore-light/20 py-2 text-sm">
                <span>{d}</span><span className="font-display text-base">{h}</span>
              </div>
            ))}
          </div>
          <div className="eyebrow text-kore-light mt-8">Group coaching hours</div>
          <div className="mt-4 text-kore-warm/85 leading-relaxed">
            {COACHING_HOURS.map(([d, h]) => (
              <div key={d} className="flex justify-between border-b border-kore-light/20 py-2 text-sm">
                <span>{d}</span><span className="font-display text-base">{h}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-kore-warm/60 leading-relaxed">
            Members with open-gym access can train freely during open gym hours. Group coaching runs in the mornings, before the floor opens for open gym.
          </p>
          <a href="/kore-timetable.pdf" download data-testid="timetable-download-pdf" className="btn-gold mt-8 inline-flex">Download the timetable (PDF) <Download size={14} /></a>
        </div>

        <div className="rounded-3xl border border-kore-gold/25 p-8 md:p-10 flex flex-col justify-between">
          <div>
            <div className="eyebrow">Ready to book?</div>
            <h2 className="display-2 mt-3 text-kore-forest">Live calendar<br/>on GoTeamUp.</h2>
            <p className="mt-4 text-kore-ink/70 leading-relaxed max-w-md">
              The interactive booking calendar shows the latest availability, holidays and any last-minute changes. Sign up once and you'll be able to book every future session in a click.
            </p>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/book" className="btn-primary">Open the calendar <ArrowRight size={14} /></Link>
            <Link to="/memberships" className="btn-ghost">Membership options</Link>
          </div>
        </div>
      </section>

      <section className="container-kore pb-24">
        <div className="rounded-3xl bg-kore-light/40 p-6 md:p-8 flex items-start gap-4">
          <Info size={18} className="text-kore-forest mt-1 shrink-0" />
          <div className="text-sm text-kore-ink/80 leading-relaxed">
            <strong>Times may vary week to week.</strong> Public holidays and coach cover can occasionally shift a session — the live calendar on <Link to="/book" className="underline decoration-kore-gold underline-offset-2">/book</Link> is always the source of truth.
          </div>
        </div>
      </section>
    </div>
  );
}
